import React, { useEffect, useState } from 'react';
import type { Region } from '@sweetgreen/garnish';
import {
  EMPTY_REGION,
  useBreakpointHelpers,
  useEffectSkipped,
  useUserLocation,
} from '@sweetgreen/garnish';

import { useCustomer } from '@order/Customer';
import { useFeatureFlag } from '@order/LaunchDarkly';
import {
  telemetryLocationFromInput,
  useTelemetry,
  useTrackEventEffect,
} from '@order/Telemetry';

import { useBagRestaurantIds } from '../../components/Bag/Bag.hooks';
import {
  useCart,
  useDeliveryInFlightWarning,
  useDeliveryOrderInFlight,
  useLocationWarningModal,
} from '../../hooks';
import { useConflictReview, useReorderState } from '../ReorderingScreen/hooks';
import {
  useCallbacks,
  useDelivery,
  useLocationCardsHeights,
  useLocationSearch,
  useLocationSearchType,
  useLocationsMapOffset,
  useNavigateToMenuFromLocation,
  usePins,
  useSearchAreaBtn,
} from './hooks';
import { DEFAULT_REGION } from './LocationScreen.utils';

export const useLocationScreenData = () => {
  // ───────────────────────────── State ─────────────────────────────────
  const [searchString, setSearchString] = React.useState('');
  const [selectedLocation, setSelectedLocation] = React.useState('');
  const [region, setRegion] = React.useState<Region>(EMPTY_REGION);
  const [shouldShowSingleLocation, setShouldShowSingleLocation] =
    React.useState(false);
  const [isShowingLocationDetails, setShowLocationDetails] =
    React.useState(false);
  const [isLocationsBottomSheetCollapsed, setIsLocationsBottomSheetCollapsed] =
    useState(true);

  // ───────────────────────────── Hooks ─────────────────────────────────
  const { currentBreakpoint } = useBreakpointHelpers();
  const { isMobile } = currentBreakpoint;
  const { customer, isFetchingCustomer, setCustomerData } = useCustomer();
  const { locationSearchType, setLocationSearchType } = useLocationSearchType();
  const { currentStep } = useReorderState();
  const conflictReview = useConflictReview();
  const { cart } = useCart();
  const { bagAddressId, bagRestaurantSlug } = useBagRestaurantIds();

  const userLocation = useUserLocation({ pause: true });
  const {
    geolocation: userLatLng,
    getUserGeolocation,
    permissionStatus: geolocationPermissionStatus,
  } = userLocation;

  const openRestaurant = useNavigateToMenuFromLocation();
  const customerAddresses = customer?.addresses;

  const {
    locations,
    locationsForSelectedSearchType,
    locationsForOppositeSearchType,
    hasResults,
    locationQueryType,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    lat,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    lng,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    zoom,
    isFetching: fetchingLocations,
    error,
    fetchLocationsBasedOnUserGeolocation,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    locationsByBoundingBox,
    updateViewportBounds,
    updateSearchViewportBounds,
    setLocationQueryType,
    ...locationData
  } = useLocationSearch({
    searchString,
    locationSearchType,
    userLocation,
    setRegion,
    getUserGeolocation,
  });

  const {
    deliveryPin,
    googlePlacesAddress,
    foundLocation,
    noLocationFound,
    predictionsData,
    isAddingAddress,
    fetchingDelivery,
    predictionsSearchString,
    setPredictionsData,
    setPredictionsSearchString,
    onOrderNow,
    onResetDeliverySearch,
    onOrderPickup,
    onPredictionPress,
    changeDeliveryRegion,
  } = useDelivery({
    searchString,
    locationSearchType,
    customer,
    isFetchingCustomer,
    setCustomerData,
    setLocationSearchType,
    setLocationQueryType,
    setSearchString,
    setRegion,
  });

  const {
    inFlightDeliveryOrder,
    deliveryInFlightWarning,
    showDeliveryInFlightWarning,
    hideDeliveryInFlightWarning,
  } = useDeliveryInFlightWarning();

  useDeliveryOrderInFlight(); // results later used from cache.

  const {
    isShownLocationWarningModal,
    showLocationWarningModal,
    hideLocationWarningModal,
    setLocationWarningCookie,
    checkShowWarning,
  } = useLocationWarningModal();

  const {
    isShown: showSearchAreaBtn,
    toggleBtnBasedOnRegion: toggleSearchAreaBtnBasedOnRegion,
    hideBtn: hideSearchAreaBtn,
  } = useSearchAreaBtn({
    region,
    isDelivery: locationSearchType === 'delivery',
    hasNoResults: !locations?.length,
  });

  const {
    locationCardsHeights,
    storeLocationCardHeight,
    firstLocationCardHeight,
  } = useLocationCardsHeights({
    searchString,
    locations: locationsForSelectedSearchType,
    locationSearchType,
    startsWithDividerTitle: locationsForSelectedSearchType?.length === 0,
  });

  const pins = usePins({
    deliveryPin,
    locationSearchType,
    selectedLocation,
    locations,
    userGeolocation: userLatLng,
  });

  // ─────────────────────────── Derived Data ─────────────────────────────
  const noResults = locationData.data && !hasResults;
  const hasNoLocations = locations.length === 0;
  const hasDelivery = Boolean(foundLocation);
  const searchByRegion = locationQueryType === 'region';
  const searchMethod = searchByRegion ? 'area-button' : 'search-field';
  const geolocationEnabled = geolocationPermissionStatus === 'granted';
  const loading = fetchingLocations || fetchingDelivery;

  // ─────────────────────────── Callbacks ───────────────────────────────
  const {
    onSearchInputSubmit,
    onSearchAreaBtnPress,
    onRegionChange,
    onMapPress,
  } = useCallbacks({
    setLocationQueryType,
    setSearchString,
    setSelectedLocation,
    toggleSearchAreaBtnBasedOnRegion,
    updateViewportBounds,
    updateSearchViewportBounds,
  });

  const onPinPress = React.useCallback(
    (restaurantSlug: string) => {
      if (locationSearchType === 'delivery') return;
      const selectedPin = pins.find((pin) => pin?.id === restaurantSlug);

      setSelectedLocation(restaurantSlug);
      setShouldShowSingleLocation(Boolean(selectedPin?.isMinimized));
    },
    [locationSearchType, pins],
  );

  const onCalloutPress = React.useCallback(
    async (props: LocationSelectionProps) => {
      if (locationSearchType === 'delivery') return;

      if (!props.restaurantSlug) return;

      // Block callout press if it has an outpost warning.
      const location = locations.find(
        (el) => el.restaurantSlug === props.restaurantSlug,
      );
      const shouldShowLocationWarning = await checkShowWarning(location);

      if (shouldShowLocationWarning) {
        setSelectedLocation(props.restaurantSlug);
        showLocationWarningModal();

        return;
      }

      const isSameRestaurantSlug = bagRestaurantSlug === props.restaurantSlug;
      const hasBag = Boolean(bagRestaurantSlug) || Boolean(bagAddressId);
      const needsToReorder = hasBag && !isSameRestaurantSlug;

      // if your bag belongs to a different restaurant, start reorder flow
      if (needsToReorder) {
        void conflictReview({
          orderId: cart?.id,
          orderType: location?.isOutpost ? 'outpost' : 'pickup',
          restaurantId: props.restaurantId,
          restaurantSlug: props.restaurantSlug,
          restaurantName: props.restaurantName,
          finishOnMenu: true,
        });
      } else {
        openRestaurant({
          ...props,
          orderType: location?.isOutpost ? 'outpost' : 'pickup',
        });
      }
    },
    [
      cart?.id,
      locationSearchType,
      locations,
      bagRestaurantSlug,
      bagAddressId,
      showLocationWarningModal,
      checkShowWarning,
      conflictReview,
      openRestaurant,
    ],
  );

  const onLocationWarningPress = React.useCallback(
    async (props: LocationSelectionProps) => {
      hideLocationWarningModal();
      const location = locations.find(
        (el) => el.restaurantSlug === props?.restaurantSlug,
      );

      await setLocationWarningCookie(location);
      openRestaurant({
        ...props,
        orderType: location?.isOutpost ? 'outpost' : 'pickup',
      });
    },
    [
      locations,
      hideLocationWarningModal,
      setLocationWarningCookie,
      openRestaurant,
    ],
  );
  const onInfoPress = React.useCallback((id: string) => {
    setSelectedLocation(id);
    setShowLocationDetails(true);
    setShouldShowSingleLocation(false);
  }, []);

  const onCardFocus = React.useCallback(
    (restaurantSlug?: string) => {
      if (!restaurantSlug) return;

      const selectedPin = pins.find(
        (pin) => pin?.restaurantSlug === restaurantSlug,
      );

      if (selectedPin) {
        onPinPress(selectedPin.id);
      }
    },
    [onPinPress, pins],
  );

  // ─── Map helpers ────────────────────────────────────────────────────────

  const shouldUseLocationsResultsRedesign =
    useFeatureFlag('XP-3156-short-locations-results-redesign') && isMobile;
  const mapOffsetForBottomSheet = useLocationsMapOffset({
    firstLocationCardHeight,
    isDeliverySearchType: locationSearchType === 'delivery',
    locations: loading ? [] : locations,
    isLocationsBottomSheetCollapsed,
  });
  const mapOffsetForList = 150;
  const mapOffset = shouldUseLocationsResultsRedesign
    ? mapOffsetForList
    : mapOffsetForBottomSheet;

  // ────────────────────────── Default Region ────────────────────────────────
  const { lat: defaultLat, lng: defaultLng } = DEFAULT_REGION;
  const isDefaultLatitude = region.lat === defaultLat;
  const isDefaultLongitude = region.lng === defaultLng;
  const isEmptyRegion = region.lat === 0 && region.lng === 0;
  const isDefaultRegion = isDefaultLatitude && isDefaultLongitude;

  useEffect(() => {
    const hasUserLocation = userLatLng?.latitude && userLatLng?.longitude;
    const userRegion = {
      ...DEFAULT_REGION,
      lat: userLatLng?.latitude ?? 0,
      lng: userLatLng?.longitude ?? 0,
    };

    // Set default region if none is set and the geolocation request is done.
    if (isEmptyRegion && !loading && hasNoLocations) {
      setRegion(hasUserLocation ? userRegion : DEFAULT_REGION);
    }

    // Set default region to user location if available.
    if (isDefaultRegion && !loading && hasNoLocations && hasUserLocation) {
      setRegion(userRegion);
    }
  }, [isEmptyRegion, isDefaultRegion, loading, userLatLng, hasNoLocations]);

  // ──────────────────────── Telemetry Effects ───────────────────────────────

  const { track } = useTelemetry();

  useTrackEventEffect({
    name: 'location.view',
    payload: { orderType: locationSearchType },
    skip: currentStep === 'locationSearch',
  });

  useEffectSkipped(() => {
    track('location.search', {
      resultsReturned: hasResults,
      searchMethod,
      geolocationEnabled,
    });
  }, [hasResults, track]);

  useEffectSkipped(() => {
    track('location.search', {
      resultsReturned: hasDelivery,
      searchMethod,
      geolocationEnabled,
    });
  }, [hasDelivery, track]);

  useEffectSkipped(() => {
    track('location.channel_changed', { channel: locationSearchType });
  }, [locationSearchType]);

  React.useEffect(() => {
    if (!noLocationFound) return;
    track('location.delivery_unavailable');
  }, [noLocationFound, track]);

  // ─────────────────────── Telemetry Callbacks─────────────────────────────

  const onMonitoredInfoPress = React.useCallback(
    (id: string) => {
      const location = locations.find((el) => el.id === id);

      track('location.details', { locationName: location?.name });
      onInfoPress(id);
    },
    [locations, onInfoPress, track],
  );

  const onMonitoredPinPress = React.useCallback(
    (id: string) => {
      const isCustomerPin = id === 'customer';

      if (isCustomerPin) return;

      track('location.pin_selected');

      onPinPress(id);
    },
    [onPinPress, track],
  );

  const onMonitoredCalloutPress = React.useCallback(
    async (props: LocationSelectionProps) => {
      const { restaurantId: id, restaurantSlug } = props;
      const isCustomerPin = id === 'customer';

      if (isCustomerPin) return;

      const location = locations.find(
        (el) => el.restaurantSlug === restaurantSlug,
      );

      track('location.open_menu', {
        location: telemetryLocationFromInput(location),
        bottomSheetOpen: isMobile,
        entryPoint: 'pin',
      });

      await onCalloutPress(props);
    },
    [isMobile, locations, track, onCalloutPress],
  );

  const onMonitoredCardPress = React.useCallback(
    async (props: LocationSelectionProps) => {
      const location = locations.find(
        (el) => el.restaurantSlug === props.restaurantSlug,
      );

      track('location.open_menu', {
        location: telemetryLocationFromInput(location),
        bottomSheetOpen: isMobile,
        entryPoint: 'card',
      });

      await onCalloutPress(props);
    },
    [locations, isMobile, track, onCalloutPress],
  );

  const onMonitoredCardFocus = React.useCallback(
    (restaurantSlug?: string) => {
      track('location.pin_selected');
      onCardFocus(restaurantSlug);
    },
    [onCardFocus, track],
  );

  const onMonitoredLocationWarningPress = React.useCallback(
    async (props: LocationSelectionProps) => {
      hideLocationWarningModal();
      const location = locations.find(
        (el) => el.restaurantSlug === props?.restaurantSlug,
      );

      await setLocationWarningCookie(location);

      track('location.open_menu', {
        location: telemetryLocationFromInput(location),
        bottomSheetOpen: isMobile,
        entryPoint: 'location-warning',
      });

      void onLocationWarningPress(props);
    },
    [
      locations,
      isMobile,
      hideLocationWarningModal,
      setLocationWarningCookie,
      track,
      onLocationWarningPress,
    ],
  );

  const onMonitoredOrderNow = React.useCallback(() => {
    if (inFlightDeliveryOrder) {
      showDeliveryInFlightWarning();

      return;
    }

    track('location.open_menu', {
      location: telemetryLocationFromInput(googlePlacesAddress),
      bottomSheetOpen: false,
      entryPoint: 'delivery-cta',
    });

    onOrderNow();
  }, [
    googlePlacesAddress,
    inFlightDeliveryOrder,
    onOrderNow,
    track,
    showDeliveryInFlightWarning,
  ]);

  // ─── Submit Handlers ─────────────────────────────────────────────

  // handle "pickup" and "outpost" search submit
  const onSearchSubmit = React.useCallback(
    (submittedSearchString: string) => {
      // on search submit, we must reset the active delivery location
      // state to avoid having different results for each search type.
      onResetDeliverySearch();

      onSearchInputSubmit(submittedSearchString);
    },
    [onResetDeliverySearch, onSearchInputSubmit],
  );

  // handle "delivery" predictions search submit
  const onPredictionsSearchSubmit = setSearchString;

  // ─────────────────────────────────────────────────────────────────

  return {
    searchString,
    locationSearchType,
    region,
    userLocation,
    locations,
    locationsForSelectedSearchType,
    locationsForOppositeSearchType,
    pins,
    predictionsData,
    predictionsSearchString,
    googlePlacesAddress,
    selectedLocation,
    shouldShowSingleLocation,
    isShowingLocationDetails,
    locationCardsHeights,
    storeLocationCardHeight,
    firstLocationCardHeight,
    showSearchAreaBtn,
    foundLocation,
    noLocationFound,
    isAddingAddress,
    customerAddresses,
    mapOffset,
    error,
    loading,
    inFlightDeliveryOrder,
    isLocationsBottomSheetCollapsed,
    deliveryInFlightWarning,
    isShownLocationWarningModal,
    fetchingDelivery,
    noResults,
    showLocationWarningModal,
    hideLocationWarningModal,
    onRegionChange,
    onMapPress,
    onResetDeliverySearch,
    onOrderPickup,
    onInfoPress: onMonitoredInfoPress,
    onPinPress: onMonitoredPinPress,
    onCalloutPress: onMonitoredCalloutPress,
    onCardPress: onMonitoredCardPress,
    onCardFocus: onMonitoredCardFocus,
    onLocationWarningPress: onMonitoredLocationWarningPress,
    onOrderNow: onMonitoredOrderNow,
    onDeliveryPress: changeDeliveryRegion,
    onPredictionPress,
    hideDeliveryInFlightWarning,
    setSelectedLocation,
    setLocationSearchType,
    setPredictionsData,
    setSearchString: onSearchInputSubmit,
    setShouldShowSingleLocation,
    setShowLocationDetails,
    setPredictionsSearchString,
    setIsLocationsBottomSheetCollapsed,
    fetchLocationsBasedOnUserGeolocation,
    onPanDrag: hideSearchAreaBtn,
    onZoomChange: hideSearchAreaBtn,
    onSearchAreaPress: onSearchAreaBtnPress,
    onSearchSubmit,
    onPredictionsSearchSubmit,
  };
};

export type LocationSelectionProps = {
  readonly restaurantId?: string;
  readonly restaurantSlug?: string;
  readonly restaurantName?: string;
};
